import React, { Component } from "react";
import { Row, Col, Modal } from "react-bootstrap";
import { withRouter } from "react-router-dom";
import { withApollo } from "react-apollo";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";
import AdmissionFormsHome from "./AdmissionFormsHome";
import AllEnquiryStudentsTab from "./AllEnquiryStudentsTab";
import AdmissionFormBuilder from "../newAdmissionForm/AdmissionFormBuilder";
import DynamicAdmissionModal from "../newAdmissionForm/DynamicAdmissionModal";
import AdmissionViewModal from "../newAdmissionForm/AdmissionViewModal";
import CardLessDataTable from "../datatables/CardLessDataTableWithSearch";
import DateBlock from "../home_bck/DateBlock";
import { ADM_MODAL_HEADER, ADM_MODAL_TITLE, ADM_MODAL_TITLE_SUB, ADM_MODAL_BODY_FLUSH, ADM_MODAL_HEADER_DOT } from "./admissionModalStyles";
import { scopeBranches, onBranchScopeChange } from "../../../utils/branchScope";
import { attachModuleGuard, AccessDeniedModal } from "../../../utils/moduleGuard";

// The Admission Forms area is the "admission_form" module. It has three grant rows split
// by sub_module_name:
//   "list"    → Admission List (view only here)
//   "builder" → Create / Edit Admission Form (the form-template builder)
//   "create"  → Add Admission (admit a student via the dynamic form)
// Each write action is scoped to its own sub-module so a grant on one can't leak into
// another (can() grants an action when ANY matched row allows it — see modulePermissions).
const ADMISSION_FORM_MODULE = ["admission_form"];
const ADMISSION_SUB_BUILDER = "builder";
const ADMISSION_SUB_CREATE = "create";

const GET_FIELD_TYPES = gql`
  query {
    getFormFieldTypes {
      id
      form_field_type
    }
  }
`;

const GET_ADMISSION_STUDENT_LIST = gql`
  query($institute_id: Int, $branch_id: String, $fromDate: String, $toDate: String) {
    getAdmissionStudentList(
      institute_id: $institute_id,
      branch_id: $branch_id,
      fromDate: $fromDate,
      toDate: $toDate
    ) {
      id
      student_name
      admission_no
      father_contact_no
      class_name
      section_name
    }
  }
`;

const GET_ADMISSION_FORMS = gql`
  query($institute_id: Int!, $branch_id: String) {
    getAdmissionForms(institute_id: $institute_id, branch_id: $branch_id) {
      id
      form_name
      academic_year_id
    }
  }
`;

const isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const todayIso = () => isoDate(new Date());
// DateBlock's built-in default is "today - 5 days" to "today" — match that
// so the displayed range and the values sent to the backend stay in sync.
const daysAgoIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return isoDate(d);
};

const GET_BRANCHES = gql`
  query getBranches($institution_id: Int!) {
    getBranches(institution_id: $institution_id) {
      id
      branch_name
      sections {
        id
        class_id
        section_name
      }
    }
  }
`;

const THEME = {
  bg: "linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)",
  primary: "#3b82f6",
  success: "#10b981",
  warning: "#f59e0b",
  danger:  "#ef4444",
  info:    "#06b6d4",
  purple:  "#7c3aed",
  text:    "#0f172a",
  textMuted: "#64748b",
  border: "#e2e8f0",
  borderLight: "#f1f5f9",
  cardShadow: "0 4px 24px rgba(0,0,0,0.05)",
};

const IconChip = ({ icon, color = "#7c3aed", bg = "#f5f3ff", border = "#ddd6fe", size = 40 }) => (
  <div style={{ width: size, height: size, borderRadius: 12, background: bg, border: `1px solid ${border}`, display: "flex", alignItems: "center", justifyContent: "center", color, fontSize: size * 0.42, flexShrink: 0 }}>
    <i className={icon} />
  </div>
);

const TABS = [
  { key: "enquiry-list",    group: "enquiry",   label: "Enquiry List",    icon: "fal fa-list-ul"     },
  { key: "enquiry-forms",   group: "enquiry",   label: "Enquiry Forms",   icon: "fal fa-folder-open" },
  { key: "admission-list",  group: "admission", label: "Admission List",  icon: "fal fa-list-ul"     },
  { key: "admission-forms", group: "admission", label: "Admission Forms", icon: "fal fa-folder-open" },
];

const NavTab = ({ active, icon, label, onClick }) => (
  <li className="nav-item">
    <a
      className={`nav-link ${active ? "active" : ""}`}
      href="#"
      onClick={(e) => { e.preventDefault(); onClick(); }}
      style={{
        cursor: "pointer",
        fontWeight: 700,
        fontSize: 14,
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "12px 20px",
        color: active ? "#6366f1" : "#4b5563",
        background: active ? "#fff" : "transparent",
        borderColor: active ? "#e2e8f0 #e2e8f0 #fff" : "transparent",
        borderWidth: "1.5px",
        borderBottomWidth: active ? "2px" : "1.5px",
        borderRadius: "10px 10px 0 0",
        transition: "all 0.15s ease",
        marginBottom: "-1.5px",
        textDecoration: "none",
      }}
    >
      <i className={icon} />
      {label}
    </a>
  </li>
);

const SectionHead = ({ icon, iconColor, iconBg, iconBorder, title, subtitle, action }) => (
  <div style={{ padding: "20px 24px", display: "flex", alignItems: "center", gap: 14, borderBottom: "1px solid " + THEME.borderLight }}>
    <IconChip icon={icon} color={iconColor} bg={iconBg} border={iconBorder} />
    <div style={{ display: "flex", flexDirection: "column" }}>
      <h5 style={{ color: THEME.text, fontWeight: 700, fontSize: 16, margin: 0, letterSpacing: "-0.01em" }}>{title}</h5>
      {subtitle && (<span style={{ color: THEME.textMuted, fontSize: 12, fontWeight: 500, marginTop: 2 }}>{subtitle}</span>)}
    </div>
    {action && <div style={{ marginLeft: "auto" }}>{action}</div>}
  </div>
);

const CreateButton = ({ onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      background: THEME.purple,
      color: "#fff",
      border: "none",
      borderRadius: 8,
      padding: "8px 16px",
      fontSize: 13,
      fontWeight: 700,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      boxShadow: "0 2px 8px rgba(124,58,237,0.25)",
    }}
  >
    <i className="fal fa-plus" />
    {label}
  </button>
);

const DEFAULT_TAB = "enquiry-list";
const VALID_TABS = TABS.map((t) => t.key);

class ManageFormsTabs extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: ManageFormsTabs.tabFromUrl(props),
      showAdmissionFormModal: false,
      admissionFormModalBranchId: "", // pre-select branch when opening from Edit
      admissionFormFieldTypes: [],
      admissionFormFieldTypesLoading: false,

      // Admission list (real backend)
      admissionList: [],
      admissionListLoading: false,
      admissionListLoaded: false,
      admissionListBranch: "", // always "" — the header's branch scope is the filter now
      admissionListStartDate: daysAgoIso(7),
      admissionListEndDate:   todayIso(),

      // Admission forms (real backend)
      admissionForms: [],
      admissionFormsLoading: false,
      admissionFormsBranch: "", // always "" — the header's branch scope is the filter now

      // Branches (from getBranches)
      branches: [],
      branchesLoading: true,

      // Dynamic Admission Form modal (per-student)
      showDynamicAdmissionModal: false,
      dynamicAdmissionStudent: null,

      // Admission view modal (read-only, per admitted student)
      showAdmissionView: false,
      admissionViewStudentId: null,

      // Flipped to true by a guard when a role without is_edit clicks a write action.
      accessDeniedShow: false,
    };

    // One guard per sub-module. They all flip the same accessDeniedShow flag, so a single
    // AccessDeniedModal serves every intercepted action. The buttons stay VISIBLE; the
    // guard intercepts the click and opens the modal when the grant is missing.
    this.builderGuard = attachModuleGuard(this, ADMISSION_FORM_MODULE, ADMISSION_SUB_BUILDER);
    this.createGuard = attachModuleGuard(this, ADMISSION_FORM_MODULE, ADMISSION_SUB_CREATE);
  }

  openAdmissionView = (eduStudentId) => {
    this.setState({ showAdmissionView: true, admissionViewStudentId: eduStudentId });
  };

  closeAdmissionView = () => {
    this.setState({ showAdmissionView: false, admissionViewStudentId: null });
  };

  openDynamicAdmission = (student) => {
    // "Add Admission" is a write (admits a student) → is_edit on the "create" sub-module.
    if (!this.createGuard.requireEdit()) return;
    this.setState({
      showDynamicAdmissionModal: true,
      dynamicAdmissionStudent: student,
    });
  };

  closeDynamicAdmission = () => {
    this.setState({
      showDynamicAdmissionModal: false,
      dynamicAdmissionStudent: null,
    });
  };

  componentDidMount() {
    this.loadBranches();
    // Branch comes from the header (NavbarOne) — re-scope and refetch when it changes.
    this.offBranchScope = onBranchScopeChange(() => this.loadBranches());
  }

  componentWillUnmount() {
    if (this.offBranchScope) this.offBranchScope();
  }

  loadBranches = async () => {
    try {
      const result = await this.props.client.query({
        query: GET_BRANCHES,
        variables: { institution_id: parseInt(Cookies.get("institutionid")) },
        fetchPolicy: "network-only",
      });
      // The header's branch selection (NavbarOne) IS the branch filter for both tabs;
      // the "All Branches" fan-out below then covers exactly what is in scope.
      const branches = scopeBranches(result.data.getBranches || []);
      this.setState({ branches, branchesLoading: false }, () => {
        if (this.state.activeTab === "admission-list") this.fetchAdmissionList("");
        if (this.state.activeTab === "admission-forms") this.fetchAdmissionForms("");
      });
    } catch {
      this.setState({ branches: [], branchesLoading: false });
    }
  };

  componentDidUpdate(prevProps, prevState) {
    // Keep the active tab in sync with the URL (back/forward, refresh, deep link).
    const urlTab = ManageFormsTabs.tabFromUrl(this.props);
    if (urlTab !== this.state.activeTab) {
      this.setState({ activeTab: urlTab });
      return;
    }

    const branchesNow = this.state.branches.length;
    const branchesBefore = prevState.branches.length;

    const tabChanged = prevState.activeTab !== this.state.activeTab;
    const branchesJustArrived = branchesBefore === 0 && branchesNow > 0;

    if (this.state.activeTab === "admission-list" && branchesNow > 0 &&
        (tabChanged || branchesJustArrived)) {
      this.fetchAdmissionList(this.state.admissionListBranch);
    }
    if (this.state.activeTab === "admission-forms" && branchesNow > 0 &&
        (tabChanged || branchesJustArrived)) {
      this.fetchAdmissionForms(this.state.admissionFormsBranch);
    }
  }

  static tabFromUrl(props) {
    const tab =
      props.match && props.match.params ? props.match.params.tab : undefined;
    return VALID_TABS.includes(tab) ? tab : DEFAULT_TAB;
  }

  // Each tab gets its own URL (/admission-forms/:tab) so tabs are
  // bookmarkable, shareable and survive a browser refresh / back-forward.
  selectTab = (key) => this.props.history.push(`/admission-forms/${key}`);

  handleAdmissionListDateRange = (startDate, endDate) => {
    this.setState({
      admissionListStartDate: startDate.format("YYYY-MM-DD"),
      admissionListEndDate:   endDate.format("YYYY-MM-DD"),
    }, () => this.fetchAdmissionList(this.state.admissionListBranch));
  };

  fetchAdmissionList = async (branchId) => {
    this.setState({ admissionListLoading: true });
    const institute_id = parseInt(Cookies.get("institutionid"));
    const branches = this.state.branches;
    const fromDate = this.state.admissionListStartDate;
    const toDate   = this.state.admissionListEndDate;
    const dateVars = {};
    if (fromDate) dateVars.fromDate = fromDate;
    if (toDate)   dateVars.toDate   = toDate;

    try {
      let rows = [];
      if (branchId) {
        const result = await this.props.client.query({
          query: GET_ADMISSION_STUDENT_LIST,
          variables: { institute_id, branch_id: String(branchId), ...dateVars },
          fetchPolicy: "network-only",
        });
        const data = result.data.getAdmissionStudentList || [];
        const branchName = (branches.find(b => String(b.id) === String(branchId)) || {}).branch_name || "";
        rows = data.map(r => ({ ...r, branch_name: branchName, branch_id: branchId }));
      } else {
        // All Branches — fan out
        const responses = await Promise.all(
          branches.map(b =>
            this.props.client.query({
              query: GET_ADMISSION_STUDENT_LIST,
              variables: { institute_id, branch_id: String(b.id), ...dateVars },
              fetchPolicy: "network-only",
            }).then(res => ({ branch: b, data: res.data.getAdmissionStudentList || [] }))
          )
        );
        rows = responses.flatMap(({ branch, data }) =>
          data.map(r => ({ ...r, branch_name: branch.branch_name, branch_id: branch.id }))
        );
      }
      this.setState({ admissionList: rows, admissionListLoading: false, admissionListLoaded: true });
    } catch {
      this.setState({ admissionList: [], admissionListLoading: false, admissionListLoaded: true });
    }
  };

  fetchAdmissionForms = async (branchId) => {
    this.setState({ admissionFormsLoading: true });
    const institute_id = parseInt(Cookies.get("institutionid"));
    const branches = this.state.branches;

    try {
      let rows = [];
      if (branchId) {
        const result = await this.props.client.query({
          query: GET_ADMISSION_FORMS,
          variables: { institute_id, branch_id: String(branchId) },
          fetchPolicy: "network-only",
        });
        const data = result.data.getAdmissionForms || [];
        const branchName = (branches.find(b => String(b.id) === String(branchId)) || {}).branch_name || "";
        rows = data.map(r => ({ ...r, branch_name: branchName, branch_id: branchId }));
      } else {
        const responses = await Promise.all(
          branches.map(b =>
            this.props.client.query({
              query: GET_ADMISSION_FORMS,
              variables: { institute_id, branch_id: String(b.id) },
              fetchPolicy: "network-only",
            }).then(res => ({ branch: b, data: res.data.getAdmissionForms || [] }))
              .catch(() => ({ branch: b, data: [] }))
          )
        );
        rows = responses.flatMap(({ branch, data }) =>
          data.map(r => ({ ...r, branch_name: branch.branch_name, branch_id: branch.id }))
        );
      }
      this.setState({ admissionForms: rows, admissionFormsLoading: false });
    } catch {
      this.setState({ admissionForms: [], admissionFormsLoading: false });
    }
  };

  openAdmissionFormModal = async (branchId = "") => {
    // Creating / editing an admission form template is a write → is_edit on the "builder"
    // sub-module. Checked before the builder modal opens, so a role without the grant gets
    // Access Denied instead of a form it could never save.
    if (!this.builderGuard.requireEdit()) return;

    // Only accept string/number branch IDs. Anything else (e.g. a click event)
    // means this was called as a raw event handler — treat as "no preselection".
    const validBranchId =
      typeof branchId === "string" || typeof branchId === "number"
        ? String(branchId)
        : "";
    this.setState({
      showAdmissionFormModal: true,
      admissionFormModalBranchId: validBranchId,
      admissionFormFieldTypesLoading: true,
    });
    try {
      const result = await this.props.client.query({
        query: GET_FIELD_TYPES,
        fetchPolicy: "network-only",
      });
      this.setState({
        admissionFormFieldTypes: result.data.getFormFieldTypes || [],
        admissionFormFieldTypesLoading: false,
      });
    } catch {
      this.setState({ admissionFormFieldTypesLoading: false });
    }
  };

  closeAdmissionFormModal = () => this.setState({
    showAdmissionFormModal: false,
    admissionFormModalBranchId: "",
    admissionFormFieldTypes: [],
    admissionFormFieldTypesLoading: false,
  }, () => {
    if (this.state.branches.length > 0) {
      this.fetchAdmissionForms(this.state.admissionFormsBranch);
    }
  });

  renderEnquiryList() {
    return <AllEnquiryStudentsTab globals={this.props.globals} />;
  }

  renderAdmissionList() {
    const { admissionList, admissionListLoading, admissionListBranch } = this.state;
    const branches = this.state.branches;
    const { admissionListStartDate, admissionListEndDate } = this.state;

    const columns = [
      { dataField: "sno",               text: "#",                formatter: (_c, _r, i) => i + 1 },
      { dataField: "admission_no",      text: "Admission No",     sort: true },
      { dataField: "student_name",      text: "Student Name",     sort: true },
      { dataField: "father_contact_no", text: "Father Contact",   sort: true },
      { dataField: "branch_name",       text: "Branch",           sort: true },
      { dataField: "class_name",        text: "Class",            sort: true, formatter: (c) => c || "-" },
      { dataField: "section_name",      text: "Section",          sort: true, formatter: (c) => c || "-" },
      {
        dataField: "action",
        text: "View",
        formatter: (_c, row) => (
          <button
            type="button"
            onClick={() => this.openAdmissionView(row.id)}
            title="View admission details"
            style={{
              border: "1px solid #c7d2fe",
              background: "#eef2ff",
              color: "#4f46e5",
              borderRadius: 8,
              padding: "4px 12px",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <i className="fas fa-eye mr-1" /> View
          </button>
        ),
      },
    ];

    return (
      <div style={{ background: "#fff", borderRadius: 16, boxShadow: THEME.cardShadow, border: "1px solid " + THEME.border, overflow: "visible" }}>
        <SectionHead
          icon="fal fa-list-ul"
          iconColor={THEME.success}
          iconBg="#dcfce7"
          iconBorder="#bbf7d0"
          title="Admission List"
          subtitle="Admitted students from the live admission backend"
          action={
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              {/* No Branch filter — the header (NavbarOne) scopes this list. */}
              <div className="adm-list-daterange" style={{ minWidth: 230, marginLeft: "auto" }}>
                <DateBlock
                  applyCallback={this.handleAdmissionListDateRange}
                  initialStart={this.state.admissionListStartDate}
                  initialEnd={this.state.admissionListEndDate}
                />
              </div>
              <span style={{ background: "#dcfce7", color: "#059669", borderRadius: 20, padding: "4px 12px", fontWeight: 600, fontSize: 12, border: "1px solid #bbf7d0" }}>
                {admissionList.length} admissions
              </span>
            </div>
          }
        />
        <div style={{ padding: "20px 24px" }}>
          {admissionListLoading ? (
            <div style={{ padding: "40px 0", textAlign: "center", color: THEME.textMuted }}>
              <div className="spinner-border text-primary" role="status" />
              <div style={{ marginTop: 10, fontSize: 13 }}>Loading admission list…</div>
            </div>
          ) : admissionList.length === 0 ? (
            <div style={{ padding: "40px 0", textAlign: "center", color: THEME.textMuted, fontSize: 13 }}>
              No admitted students found{admissionListBranch ? " for the selected branch" : ""}.
            </div>
          ) : (
            <CardLessDataTable
              parentData={admissionList}
              particlecolumns={columns}
              name="admission_list"
              placeholder="Search admissions"
            />
          )}
        </div>
      </div>
    );
  }

  renderAdmissionForms() {
    const { admissionForms, admissionFormsLoading, admissionFormsBranch } = this.state;
    const branches = this.state.branches;

    return (
      <div style={{ background: "#fff", borderRadius: 16, boxShadow: THEME.cardShadow, border: "1px solid " + THEME.border, overflow: "visible" }}>
        <SectionHead
          icon="fal fa-folder-open"
          iconColor={THEME.success}
          iconBg="#dcfce7"
          iconBorder="#bbf7d0"
          title="Admission Forms"
          subtitle="Admission form templates"
          action={
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {/* No Branch filter — the header (NavbarOne) scopes these forms. */}
              <CreateButton onClick={() => this.openAdmissionFormModal()} label="Create Admission Form" />
            </div>
          }
        />
        <div style={{ padding: "20px 24px" }}>
          {admissionFormsLoading ? (
            <div style={{ padding: "40px 0", textAlign: "center", color: THEME.textMuted }}>
              <div className="spinner-border text-primary" role="status" />
              <div style={{ marginTop: 10, fontSize: 13 }}>Loading admission forms…</div>
            </div>
          ) : admissionForms.length === 0 ? (
            <div style={{ padding: "40px 0", textAlign: "center", color: THEME.textMuted, fontSize: 13 }}>
              No admission forms found{admissionFormsBranch ? " for the selected branch" : ""}.
              Use <strong>Create Admission Form</strong> to add one.
            </div>
          ) : (
            (() => {
              // Group by branch — same layout as the Enquiry Forms list
              const groupsMap = {};
              admissionForms.forEach((f) => {
                const key = String(f.branch_id);
                if (!groupsMap[key]) {
                  groupsMap[key] = { branch_id: f.branch_id, branch_name: f.branch_name, forms: [] };
                }
                groupsMap[key].forms.push(f);
              });
              const groups = Object.values(groupsMap);
              // Show newly added/updated forms last: backend returns id DESC,
              // so order each branch's forms by id ascending (newest at the bottom).
              groups.forEach((g) => g.forms.sort((a, b) => Number(a.id) - Number(b.id)));
              const totalForms = admissionForms.length;

              return (
                <>
                  <p className="af-results-summary">
                    {totalForms} form{totalForms !== 1 ? "s" : ""}
                    &nbsp;&middot;&nbsp;
                    {groups.length} branch{groups.length !== 1 ? "es" : ""}
                  </p>

                  {groups.map((group) => (
                    <div key={group.branch_id} className="af-branch-group">
                      <div className="af-branch-banner">
                        <i className="fas fa-building" />
                        {group.branch_name}
                        <span className="af-branch-count">
                          {group.forms.length} form{group.forms.length !== 1 ? "s" : ""}
                        </span>
                      </div>

                      <div className="af-smart-list">
                        {group.forms.map((f) => (
                          <div className="af-list-row-container" key={`${f.branch_id}-${f.id}`}>
                            <div className="af-list-row">
                              <div className="af-row-main">
                                <div className="af-row-icon">
                                  <i className="fas fa-file-signature" />
                                </div>
                                <div className="af-row-info">
                                  <h6>{f.form_name}</h6>
                                  <div className="af-row-sub">
                                    <span><i className="fas fa-hashtag mr-1" /> Form ID {f.id}</span>
                                    {f.academic_year_id != null && (
                                      <>
                                        <span>&bull;</span>
                                        <span><i className="fas fa-calendar-alt mr-1" /> AY {f.academic_year_id}</span>
                                      </>
                                    )}
                                    <span>&bull;</span>
                                    <span><i className="fas fa-building mr-1" /> {group.branch_name}</span>
                                  </div>
                                </div>
                              </div>

                              <div className="af-row-actions">
                                <button
                                  className="af-action-pill-btn edit-btn"
                                  onClick={() => this.openAdmissionFormModal(f.branch_id)}
                                  title="Edit Form Form"
                                >
                                  <i className="fas fa-sliders-h" />
                                  <span>Edit Admission Form</span>
                                </button>

                                <div className="af-action-divider" />

                                <button
                                  className="af-primary-action-btn"
                                  onClick={() => this.openDynamicAdmission({
                                    id: 0,
                                    branch_id: f.branch_id,
                                    student_name: "",
                                    student_mobile: "",
                                    student_email: "",
                                    gender: "",
                                    dob: "",
                                    father_name: "",
                                    father_mobile: "",
                                    mother_name: "",
                                    mother_mobile: "",
                                    address: "",
                                  })}
                                  title="Add a new student directly without an enquiry"
                                >
                                  <i className="fas fa-plus-circle" />
                                  Add Admission
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              );
            })()
          )}
        </div>
      </div>
    );
  }

  renderActivePane() {
    const { activeTab } = this.state;
    if (activeTab === "enquiry-list")    return this.renderEnquiryList();
    if (activeTab === "enquiry-forms")   return <AdmissionFormsHome {...this.props} />;
    if (activeTab === "admission-list")  return this.renderAdmissionList();
    if (activeTab === "admission-forms") return this.renderAdmissionForms();
    return null;
  }

  render() {
    const { activeTab, showAdmissionFormModal, admissionFormFieldTypes, admissionFormFieldTypesLoading } = this.state;

    return (
      <div style={{ background: THEME.bg, minHeight: "100vh", padding: "24px 28px", paddingBottom: 40 }}>
        <style>{`* { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif; }`}</style>

        <div style={{ background: "#fff", borderRadius: 16, boxShadow: THEME.cardShadow, overflow: "visible", border: "1px solid " + THEME.border }}>
          {/* Single-tier finance-style folder tabs, with a group divider between Enquiry and Admission */}
          <div style={{ background: "#f8fafc", borderBottom: "1.5px solid " + THEME.border, padding: "12px 24px 0 24px", borderTopLeftRadius: 16, borderTopRightRadius: 16 }}>
            <ul className="nav nav-tabs" style={{ borderBottom: "none", gap: 8, alignItems: "flex-end" }}>
              {TABS.map((t, i) => {
                const prev = TABS[i - 1];
                const showDivider = prev && prev.group !== t.group;
                return (
                  <React.Fragment key={t.key}>
                    {showDivider && (
                      <li style={{ alignSelf: "stretch", display: "flex", alignItems: "center", padding: "0 6px" }}>
                        <span style={{ width: 1, height: 22, background: THEME.border, display: "inline-block" }} />
                      </li>
                    )}
                    <NavTab active={activeTab === t.key} icon={t.icon} label={t.label} onClick={() => this.selectTab(t.key)} />
                  </React.Fragment>
                );
              })}
            </ul>
          </div>

          {/* Content panel */}
          <div style={{ padding: "24px" }}>
            {this.renderActivePane()}
          </div>
        </div>

        {/* Create / Edit Admission Form Modal */}
        <Modal
          show={showAdmissionFormModal}
          onHide={this.closeAdmissionFormModal}
          centered
          size="xl"
          backdrop="static"
        >
          <Modal.Header closeButton style={ADM_MODAL_HEADER}>
            <Modal.Title style={ADM_MODAL_TITLE}>
              <span style={ADM_MODAL_HEADER_DOT} />
              <i className="fal fa-file-signature" />
              <span>
                Create / Edit Admission Form
                <span style={ADM_MODAL_TITLE_SUB}>Build the admission form structure and fields</span>
              </span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={ADM_MODAL_BODY_FLUSH}>
            {showAdmissionFormModal && (
              admissionFormFieldTypesLoading ? (
                <div style={{ padding: "40px 24px", textAlign: "center", color: THEME.textMuted }}>
                  <div className="spinner-border text-primary" role="status" />
                  <div style={{ marginTop: 12, fontSize: 13 }}>Loading form builder…</div>
                </div>
              ) : (
                <div style={{ padding: "20px 24px" }}>
                  <AdmissionFormBuilder
                    branches={this.state.branches}
                    fieldTypes={admissionFormFieldTypes}
                    initialBranchId={this.state.admissionFormModalBranchId}
                    onSaved={this.closeAdmissionFormModal}
                  />
                </div>
              )
            )}
          </Modal.Body>
        </Modal>

        {/* Dynamic Admission Form Modal (per-student view from Admission List) */}
        <DynamicAdmissionModal
          show={this.state.showDynamicAdmissionModal}
          onHide={this.closeDynamicAdmission}
          student={this.state.dynamicAdmissionStudent}
        />

        {/* Read-only admission details view (merges edu_students + admission_field_value) */}
        <AdmissionViewModal
          show={this.state.showAdmissionView}
          onHide={this.closeAdmissionView}
          eduStudentId={this.state.admissionViewStudentId}
        />

        {/* Shown when a role without is_edit clicks Create / Edit Admission Form or Add
            Admission. Both sub-module guards flip the same accessDeniedShow flag. */}
        <AccessDeniedModal
          show={this.state.accessDeniedShow}
          onHide={this.builderGuard.hideAccessDenied}
        />
      </div>
    );
  }
}

export default withRouter(withApollo(ManageFormsTabs));
